import { X } from "lucide-react";
import { KitIconButton } from "../../components/kit/IconButton";
import { KitModal } from "../../components/kit/Modal";
import { KitSegmentedControl } from "../../components/kit/SegmentedControl";
import AddPostForm, { type PillarChoice } from "./AddPostForm";
import { CalendarSheet } from "./CalendarSheet";
import type { NewPost } from "./MonthGrid";

type Kind = "planned" | "quick";

/**
 * Add a post, from its day (DESIGN.md "Adding a post starts from its day"). The
 * planned-or-quick choice is a segmented control; a planned post is the add
 * form, a quick post hands over to the quick post dialog with the same day. On
 * a phone it is a large sheet (e3dUbA) and the fields read at 16px.
 */
export default function AddPostDialog({
  year,
  month,
  day,
  pillars,
  onAdd,
  onQuick,
  onClose,
  phone = false,
}: {
  year: number;
  month: number;
  day: number;
  pillars?: PillarChoice[];
  onAdd: (post: NewPost) => Promise<void> | void;
  /** Absent when quick posts cannot be made here. */
  onQuick?: (day: number) => void;
  onClose: () => void;
  phone?: boolean;
}) {
  const kind = onQuick ? (
    <KitSegmentedControl<Kind>
      variant="lens"
      size={phone ? "large" : "default"}
      ariaLabel="Kind of post"
      options={[
        { label: "Planned post", value: "planned" },
        { label: "Quick post", value: "quick" },
      ]}
      value="planned"
      onChange={(next) => {
        if (next === "quick") onQuick(day);
      }}
      className="self-start"
    />
  ) : null;
  const lede = onQuick
    ? "Pick the kind of post, then its day and format."
    : "A working title, its day and format. The ideas come later.";

  if (phone) {
    return (
      <CalendarSheet phone large label="Add a post" onClose={onClose}>
        <div className="flex flex-col gap-5">
          <header className="flex items-start justify-between gap-2">
            <div className="flex min-w-0 flex-col gap-1 pt-1">
              <h2 className="font-section text-[26px] font-bold leading-tight text-co-text">Add a post</h2>
              <p className="text-[15px] leading-snug text-co-text2">{lede}</p>
            </div>
            <KitIconButton kind="close" size="xl" icon={X} iconClassName="size-3.5" aria-label="Close" onClick={onClose} />
          </header>
          {kind}
          <AddPostForm bare phone year={year} month={month} day={day} pillars={pillars} onAdd={onAdd} onClose={onClose} />
        </div>
      </CalendarSheet>
    );
  }

  return (
    <KitModal
      isOpen
      hideCloseButton={false}
      aria-label="Add a post"
      onOpenChange={(open) => !open && onClose()}
      modalClassName="max-w-[680px]"
    >
      <div className="flex flex-col gap-5">
        <header className="flex flex-col gap-1">
          <h2 className="font-section text-[22px] font-bold leading-tight text-co-text">Add a post</h2>
          <p className="text-[13.5px] text-co-text2">{lede}</p>
        </header>
        {kind}
        <AddPostForm bare year={year} month={month} day={day} pillars={pillars} onAdd={onAdd} onClose={onClose} />
      </div>
    </KitModal>
  );
}
